import { type FC } from "react";
import { FileIcon } from "lucide-react";

import { formatDate } from "@/shared/utils/formatters";
import { Skeleton } from "@/shared/components/ui/skeleton";
import type { DocumentReference } from "@/shared/hooks/useItemDocumentReferences";

interface RelatedDocsSectionProps {
  docRefs: DocumentReference[];
  isLoading: boolean;
  onDocumentClick?: (doc: DocumentReference) => void;
}

export const RelatedDocsSection: FC<RelatedDocsSectionProps> = ({
  docRefs,
  isLoading,
  onDocumentClick,
}) => {
  return (
    <div className="px-4 py-4 sm:px-6 sm:py-6">
      <div className="mb-4">
        <h4>Related documents</h4>
      </div>
      {isLoading ? (
        <div className="flex flex-col gap-3">
          <Skeleton className="h-16 w-full rounded-lg" />
          <Skeleton className="h-16 w-full rounded-lg" />
        </div>
      ) : docRefs.length === 0 ? (
        <p className="text-sm text-tertiary">No documents attached.</p>
      ) : (
        <div className="flex flex-col gap-3">
          {docRefs.map((ref) => {
            const displayName = ref.display_name || ref.file_name || "Document";
            return (
              <div
                key={ref.id}
                role={onDocumentClick ? "button" : undefined}
                tabIndex={onDocumentClick ? 0 : undefined}
                onClick={() => onDocumentClick?.(ref)}
                onKeyDown={(e) => {
                  if (onDocumentClick && (e.key === "Enter" || e.key === " ")) {
                    e.preventDefault();
                    onDocumentClick(ref);
                  }
                }}
                className={`flex items-center gap-3 rounded-lg border border-primary bg-subtle/60 p-4${onDocumentClick ? " cursor-pointer transition-colors hover:bg-subtle" : ""}`}
              >
                <FileIcon className="h-8 w-8 shrink-0 text-stone-300" strokeWidth={1} />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm">{displayName}</p>
                  <p className="text-2xs text-secondary">{formatDate(ref.document_created_at)}</p>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
